if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.kkPreserveMediaOrder = [
          'bryllup-gjestebok',
          'dap-gjestebok',
          'konfirmasjon-gjestebok',
          'hyttebok-premium',
          'bobilbok-premium',
          'tilbehor-hytte-bobil-premium',
          'tilbehor-premium',
          'skilt-premium',
        ].includes(document.body.dataset.kkTemplate);
        this.captureMediaOrder();
        this.bindCentralNavigation();
        this.bindGalleryInteractionTracking();
        this.updateNavigationDiagnostics();
        if (
          this.elements.thumbnails &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      captureMediaOrder() {
        if (!this.kkPreserveMediaOrder) return;
        this.kkViewerMediaOrder = this.getMediaItems().map((item) => item.dataset.mediaId);
        this.kkThumbnailMediaOrder = Array.from(
          this.elements.thumbnails?.querySelectorAll('[data-target]') || []
        ).map((item) => item.dataset.target);
      }

      restoreMediaOrder() {
        if (!this.kkPreserveMediaOrder) return false;

        const viewerList = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        const thumbnailList = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        const viewerOrder = Array.from(viewerList?.children || [])
          .map((item) => item.dataset.mediaId)
          .filter(Boolean);
        const thumbnailOrder = Array.from(thumbnailList?.children || [])
          .map((item) => item.dataset.target)
          .filter(Boolean);
        const expectedViewerOrder = this.completeMediaOrder(this.kkViewerMediaOrder, viewerOrder);
        const expectedThumbnailOrder = this.completeMediaOrder(
          this.kkThumbnailMediaOrder,
          thumbnailOrder
        );
        const viewerMatches = this.mediaOrdersMatch(viewerOrder, expectedViewerOrder);
        const thumbnailsMatch = this.mediaOrdersMatch(thumbnailOrder, expectedThumbnailOrder);
        if (viewerMatches && thumbnailsMatch) return false;

        const viewerChanged = this.reorderMediaChildren(viewerList, expectedViewerOrder, 'mediaId');
        const thumbnailsChanged = this.reorderMediaChildren(thumbnailList, expectedThumbnailOrder, 'target');
        if (viewerChanged) this.elements.viewer?.resetPages?.();
        if (thumbnailsChanged) this.elements.thumbnails?.resetPages?.();
        return viewerChanged || thumbnailsChanged;
      }

      completeMediaOrder(expectedOrder = [], currentOrder = []) {
        const known = new Set(expectedOrder);
        return [
          ...expectedOrder.filter((mediaId) => currentOrder.includes(mediaId)),
          ...currentOrder.filter((mediaId) => !known.has(mediaId)),
        ];
      }

      mediaOrdersMatch(currentOrder = [], expectedOrder = []) {
        return (
          currentOrder.length === expectedOrder.length &&
          currentOrder.every((mediaId, index) => mediaId === expectedOrder[index])
        );
      }

      reorderMediaChildren(list, expectedOrder, datasetKey) {
        if (!list || !expectedOrder.length) return false;
        let changed = false;
        expectedOrder.forEach((mediaId, index) => {
          const current = list.children[index];
          if (current?.dataset?.[datasetKey] === mediaId) return;
          const item = Array.from(list.children).find((candidate) => candidate.dataset?.[datasetKey] === mediaId);
          if (!item) return;
          list.insertBefore(item, current || null);
          changed = true;
        });
        return changed;
      }

      findMedia(mediaId) {
        const requested = String(mediaId || '');
        const normalized = requested.split('-').pop();
        return (
          this.getMediaItems().find(
            (item) =>
              item.dataset.mediaId === requested ||
              String(item.dataset.mediaId || '').split('-').pop() === normalized
          ) || null
        );
      }

      findThumbnail(mediaId) {
        const requested = String(mediaId || '');
        const normalized = requested.split('-').pop();
        return (
          Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []).find(
            (item) =>
              item.dataset.target === requested ||
              String(item.dataset.target || '').split('-').pop() === normalized
          ) || null
        );
      }

      createGalleryTransactionId() {
        this.kkGalleryTransactionCounter = (this.kkGalleryTransactionCounter || 0) + 1;
        return `gallery-${this.id || 'media'}-${this.kkGalleryTransactionCounter}`;
      }

      normalizeMediaId(mediaId) {
        return String(mediaId || '').split('-').pop();
      }

      getLogicalActiveMediaId() {
        return this.normalizeMediaId(
          this.getMediaItems().find((item) => item.classList.contains('is-active'))?.dataset.mediaId ||
            this.elements.thumbnails
              ?.querySelector('[data-target] .thumbnail[aria-current="true"]')
              ?.closest('[data-target]')?.dataset.target
        );
      }

      getActiveMediaId() {
        return this.getLogicalActiveMediaId();
      }

      getMediaSlider() {
        return (
          this.elements.viewer?.slider ||
          this.elements.viewer?.querySelector('[id^="Slider-Gallery"]') ||
          null
        );
      }

      getMediaItems() {
        return Array.from(this.getMediaSlider()?.children || []).filter(
          (item) => item.dataset?.mediaId
        );
      }

      getTargetScrollLeft(target, slider = this.getMediaSlider()) {
        if (!target || !slider) return 0;
        const scrollPaddingLeft =
          Number.parseFloat(getComputedStyle(slider).scrollPaddingLeft) || 0;
        const maximumScrollLeft = Math.max(
          0,
          Number(slider.scrollWidth || 0) - Number(slider.clientWidth || 0)
        );
        return Math.max(
          0,
          Math.min(Number(target.offsetLeft || 0) - scrollPaddingLeft, maximumScrollLeft)
        );
      }

      getPhysicallyVisibleMediaId() {
        const slider = this.getMediaSlider();
        const mediaItems = this.getMediaItems().filter((item) => item.clientWidth > 0);
        if (!slider || !mediaItems.length) return '';

        const scrollLeft = Number(slider.scrollLeft || 0);
        const closest = mediaItems.reduce((best, item) => {
          const distance = Math.abs(this.getTargetScrollLeft(item, slider) - scrollLeft);
          return !best || distance < best.distance ? { item, distance } : best;
        }, null);
        return this.normalizeMediaId(closest?.item?.dataset.mediaId);
      }

      updateNavigationDiagnostics(transaction = this.kkActiveMediaTransaction) {
        const diagnosticTransaction =
          transaction || this.kkLastCompletedMediaTransaction || null;
        const slider = this.getMediaSlider();
        const diagnosticMediaId =
          diagnosticTransaction?.expectedMediaId || this.getLogicalActiveMediaId();
        const target = diagnosticMediaId
          ? this.findMedia(diagnosticMediaId)
          : null;
        this.dataset.kkNavigationGeneration = String(
          diagnosticTransaction?.generation ?? this.kkGalleryGeneration ?? 0
        );
        this.dataset.kkNavigationTransactionId = diagnosticTransaction?.transactionId || '';
        this.dataset.kkNavigationSource = diagnosticTransaction?.source || '';
        this.dataset.kkNavigationStatus = diagnosticTransaction?.status || 'idle';
        this.dataset.kkLogicalMediaId = this.getLogicalActiveMediaId();
        this.dataset.kkPhysicalMediaId = this.getPhysicallyVisibleMediaId();
        this.dataset.kkExpectedMediaId =
          diagnosticTransaction?.expectedMediaId ||
          this.kkLastCompletedMediaTransaction?.expectedMediaId ||
          this.getLogicalActiveMediaId();
        this.dataset.kkScrollLeft = String(Number(slider?.scrollLeft || 0));
        this.dataset.kkTargetOffsetLeft = String(this.getTargetScrollLeft(target, slider));
        this.dataset.kkThumbnailClickCount ||= '0';
        this.dataset.kkMediaNotifyCount ||= '0';
        this.dataset.kkSettleCheckCount ||= '0';
      }

      isInteractiveGalleryControl(target) {
        if (!(target instanceof Element)) return false;
        const control = target.closest(
          '.kk-gallery-arrow, button.slider-button, button.thumbnail, a, input, select, textarea, [role="button"], .deferred-media__poster'
        );
        if (control) return true;
        const modalOpener = target.closest('button.product__modal-opener');
        return Boolean(modalOpener && !target.closest('img, picture'));
      }

      bindCentralNavigation() {
        const viewer = this.elements.viewer;
        const slider = this.getMediaSlider();
        if (!viewer || !slider || this.dataset.kkGalleryBound === 'true') return;

        this.dataset.kkGalleryBound = 'true';
        this.dataset.kkControlledScroll = 'true';
        viewer.dataset.kkControlledScroll = 'true';
        slider.dataset.kkControlledScroll = 'true';

        viewer.addEventListener('slideChanged', (event) => this.onSlideChanged(event));
        viewer.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('button.slider-button');
            if (!button || !viewer.contains(button)) return;
            const direction = button.name === 'previous' ? -1 : button.name === 'next' ? 1 : 0;
            if (!direction) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.navigateByStep(direction, {
              origin: 'user',
              syncVariant: true,
              source: 'slider-button',
              loop: false,
            });
          },
          true
        );
        viewer.addEventListener('keydown', (event) => this.onGalleryKeydown(event), true);
        slider.addEventListener('scroll', () => this.onViewerScroll(), { passive: true });
        if ('onscrollend' in slider) {
          slider.addEventListener('scrollend', () => this.onViewerScrollEnd());
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.dataset.kkGalleryBound = 'true';
          this.elements.thumbnails.dataset.kkThumbnailListenerCount = '1';
          this.elements.thumbnails.addEventListener('mousedown', (event) => {
            if (event.target.closest('button.thumbnail')) event.preventDefault();
          });
          this.elements.thumbnails.addEventListener('click', (event) => {
            const button = event.target.closest('button.thumbnail');
            const target = button?.closest('[data-target]');
            if (!button || !target || !this.elements.thumbnails.contains(target)) return;
            event.preventDefault();
            event.stopPropagation();
            this.dataset.kkThumbnailClickCount = String(
              Number(this.dataset.kkThumbnailClickCount || 0) + 1
            );
            this.dataset.kkClickedMediaId = this.normalizeMediaId(target.dataset.target);
            this.navigateToMedia(target.dataset.target, {
              origin: 'user',
              syncVariant: true,
              source: 'thumbnail',
              fromMediaId: this.navigationAnchorMediaId(),
              targetMediaId: target.dataset.target,
            });
          });
        }
      }

      onGalleryKeydown(event) {
        if (
          event.target.closest('input, select, textarea, a, button:not(.slider-button)') ||
          !['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)
        ) {
          return;
        }

        const mediaItems = this.getMediaItems();
        if (!mediaItems.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();

        if (event.key === 'Home' || event.key === 'End') {
          const target = event.key === 'Home' ? mediaItems[0] : mediaItems[mediaItems.length - 1];
          this.navigateToMedia(target.dataset.mediaId, {
            origin: 'user',
            syncVariant: true,
            source: 'keyboard',
            fromMediaId: this.navigationAnchorMediaId(),
            targetMediaId: target.dataset.mediaId,
          });
          return;
        }

        const direction = ['ArrowLeft', 'PageUp'].includes(event.key) ? -1 : 1;
        this.navigateByStep(direction, {
          origin: 'user',
          syncVariant: true,
          source: 'keyboard',
          loop: false,
        });
      }

      navigationAnchorMediaId() {
        return (
          this.kkActiveMediaTransaction?.expectedMediaId ||
          this.getPhysicallyVisibleMediaId() ||
          this.getLogicalActiveMediaId()
        );
      }

      navigateByStep(step, context = {}) {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) {
          return this.mediaActionResult('missing-media', null, context);
        }

        const fromMediaId = this.navigationAnchorMediaId();
        let index = mediaItems.findIndex(
          (item) => this.normalizeMediaId(item.dataset.mediaId) === fromMediaId
        );
        if (index < 0) index = 0;
        const loop = context.loop === true;
        const rawTargetIndex = index + step;
        const targetIndex = loop
          ? (rawTargetIndex + mediaItems.length) % mediaItems.length
          : Math.max(0, Math.min(mediaItems.length - 1, rawTargetIndex));
        if (targetIndex === index) {
          return this.mediaActionResult('already-active', fromMediaId, context);
        }

        const target = mediaItems[targetIndex];
        return this.navigateToMedia(target.dataset.mediaId, {
          ...context,
          fromMediaId,
          targetMediaId: target.dataset.mediaId,
        });
      }

      bindGalleryInteractionTracking() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        viewer.addEventListener('pointerdown', (event) => this.startGalleryGesture(event, 'pointer'));
        viewer.addEventListener('pointermove', (event) => this.updateGalleryGesture(event), { passive: true });
        viewer.addEventListener('pointerup', (event) => this.finishGalleryGesture(event));
        viewer.addEventListener('pointercancel', () => this.cancelGalleryGesture());
        if (!window.PointerEvent) {
          viewer.addEventListener('touchstart', (event) => this.startGalleryGesture(event, 'touch'), {
            passive: true,
          });
          viewer.addEventListener('touchmove', (event) => this.updateGalleryGesture(event), { passive: true });
          viewer.addEventListener('touchend', (event) => this.finishGalleryGesture(event));
          viewer.addEventListener('touchcancel', () => this.cancelGalleryGesture());
        }
        viewer.addEventListener(
          'wheel',
          (event) => {
            if (
              this.isInteractiveGalleryControl(event.target) ||
              Math.abs(event.deltaX) < 2 ||
              Math.abs(event.deltaX) <= Math.abs(event.deltaY)
            ) {
              return;
            }
            this.startConfirmedGalleryGesture('wheel');
          },
          { passive: true }
        );
      }

      gesturePoint(event) {
        const point = event.touches?.[0] || event.changedTouches?.[0] || event;
        return { x: Number(point.clientX || 0), y: Number(point.clientY || 0) };
      }

      startGalleryGesture(event, source) {
        if (this.isInteractiveGalleryControl(event.target)) return;
        const point = this.gesturePoint(event);
        this.kkUserInteractionSequence = (this.kkUserInteractionSequence || 0) + 1;
        this.kkUserGesture = {
          source,
          sequence: this.kkUserInteractionSequence,
          fromMediaId: this.navigationAnchorMediaId(),
          startX: point.x,
          startY: point.y,
          moved: false,
          confirmed: false,
          active: true,
        };
      }

      updateGalleryGesture(event) {
        const gesture = this.kkUserGesture;
        if (!gesture?.active) return;
        const point = this.gesturePoint(event);
        const deltaX = Math.abs(point.x - gesture.startX);
        const deltaY = Math.abs(point.y - gesture.startY);
        if (deltaX >= 12 && deltaX > deltaY && !gesture.moved) {
          gesture.moved = true;
          gesture.confirmed = true;
          this.cancelActiveNavigation('superseded', { newerUserTransaction: true });
        }
      }

      finishGalleryGesture(event) {
        const gesture = this.kkUserGesture;
        if (!gesture?.active) return;
        this.updateGalleryGesture(event);
        gesture.active = false;
        gesture.confirmed = gesture.moved;
        if (!gesture.confirmed) {
          this.kkUserGesture = null;
          return;
        }
        this.scheduleUserScrollSettle();
      }

      cancelGalleryGesture() {
        this.kkUserGesture = null;
      }

      startConfirmedGalleryGesture(source) {
        this.kkUserInteractionSequence = (this.kkUserInteractionSequence || 0) + 1;
        this.cancelActiveNavigation('superseded', { newerUserTransaction: true });
        this.kkUserGesture = {
          source,
          sequence: this.kkUserInteractionSequence,
          fromMediaId: this.navigationAnchorMediaId(),
          moved: true,
          confirmed: true,
          active: false,
        };
      }

      scheduleUserScrollSettle() {
        if (this.kkUserScrollSettleTimer) clearTimeout(this.kkUserScrollSettleTimer);
        this.kkUserScrollSettleTimer = setTimeout(() => this.settleUserDrivenNavigation(), 90);
      }

      settleUserDrivenNavigation() {
        const gesture = this.kkUserGesture;
        if (!gesture?.confirmed || this.kkActiveMediaTransaction) return;
        const physicalMediaId = this.getPhysicallyVisibleMediaId();
        if (!physicalMediaId || physicalMediaId === gesture.fromMediaId) {
          this.kkUserGesture = null;
          return;
        }
        this.navigateToMedia(physicalMediaId, {
          origin: 'user',
          syncVariant: true,
          source: gesture.source || 'swipe',
          fromMediaId: gesture.fromMediaId,
          targetMediaId: physicalMediaId,
        });
      }

      normalizeMediaContext(context = {}) {
        const origin = ['user', 'variant', 'internal'].includes(context.origin) ? context.origin : 'internal';
        return {
          origin,
          syncVariant: context.syncVariant ?? origin === 'user',
          transactionId: context.transactionId || this.createGalleryTransactionId(),
          generation: context.generation,
          source: context.source || 'internal',
          fromMediaId: this.normalizeMediaId(context.fromMediaId || this.getActiveMediaId()),
          expectedMediaId: this.normalizeMediaId(context.expectedMediaId || context.targetMediaId),
          requestToken: Number.isFinite(Number(context.requestToken))
            ? Number(context.requestToken)
            : null,
          variantId: context.variantId ? String(context.variantId) : null,
          latestApprovedVariant: context.latestApprovedVariant === true,
        };
      }

      beginMediaTransaction(context = {}) {
        const normalizedContext = this.normalizeMediaContext(context);
        const generation = (this.kkGalleryGeneration || 0) + 1;
        this.kkGalleryGeneration = generation;
        let resolveSettled;
        const settled = new Promise((resolve) => {
          resolveSettled = resolve;
        });
        const transaction = {
          transactionId: normalizedContext.transactionId,
          generation,
          source: normalizedContext.source,
          origin: normalizedContext.origin,
          syncVariant: normalizedContext.syncVariant,
          fromMediaId: normalizedContext.fromMediaId,
          expectedMediaId: normalizedContext.expectedMediaId,
          requestToken: normalizedContext.requestToken,
          variantId: normalizedContext.variantId,
          userInteractionSequence: this.kkUserInteractionSequence || 0,
          startedAt: Date.now(),
          status: 'pending',
          notificationSent: false,
          stableObservations: 0,
          stableSince: null,
          lastObservedScrollLeft: null,
          settled,
          resolveSettled,
        };
        this.kkActiveMediaTransaction = transaction;
        this.kkUserGesture = null;
        this.revealTargetForPhysicalNavigation(transaction);
        this.updateTransactionDiagnostics(transaction);
        return this.contextFromTransaction(transaction);
      }

      contextFromTransaction(transaction) {
        return {
          origin: transaction.origin,
          syncVariant: transaction.syncVariant,
          transactionId: transaction.transactionId,
          generation: transaction.generation,
          source: transaction.source,
          fromMediaId: transaction.fromMediaId,
          expectedMediaId: transaction.expectedMediaId,
          requestToken: transaction.requestToken,
          variantId: transaction.variantId,
          settled: transaction.settled,
        };
      }

      updateTransactionDiagnostics(transaction) {
        if (!transaction) return;
        this.dataset.kkTransactionId = transaction.transactionId;
        this.dataset.kkTransactionGeneration = String(transaction.generation);
        this.dataset.kkTransactionSource = transaction.source;
        this.dataset.kkTransactionFromMediaId = transaction.fromMediaId || '';
        this.dataset.kkTransactionTargetMediaId = transaction.expectedMediaId || '';
        this.dataset.kkTransactionStatus = transaction.status;
        this.dataset.kkTransactionRequestToken = transaction.requestToken ?? '';
        this.dataset.kkNavigationGeneration = String(transaction.generation);
        this.dataset.kkNavigationTransactionId = transaction.transactionId;
        this.dataset.kkNavigationSource = transaction.source;
        this.dataset.kkNavigationStatus = transaction.status;
        this.dataset.kkExpectedMediaId = transaction.expectedMediaId || '';
        this.updateNavigationDiagnostics(transaction);
      }

      noteVariantRequest(context = {}) {
        const requestToken = Number(context.requestToken);
        if (!Number.isFinite(requestToken)) return;
        this.kkLatestVariantRequestToken = Math.max(
          this.kkLatestVariantRequestToken || 0,
          requestToken
        );
        this.kkVariantRequestTransactions = this.kkVariantRequestTransactions || new Map();
        this.kkVariantRequestTransactions.set(requestToken, {
          requestToken,
          transactionId: context.transactionId || null,
          origin: context.origin || 'variant',
          requestedAt: Number(context.requestedAt) || Date.now(),
        });
        if (this.kkVariantRequestTransactions.size > 50) {
          this.kkVariantRequestTransactions.delete(
            this.kkVariantRequestTransactions.keys().next().value
          );
        }
      }

      finishActiveMediaTransaction(transactionId = null, generation = null) {
        const transaction = this.kkActiveMediaTransaction;
        if (
          !transaction ||
          transaction.generation !== this.kkGalleryGeneration ||
          (transactionId && transaction.transactionId !== transactionId) ||
          (generation != null && transaction.generation !== generation)
        ) {
          return false;
        }
        transaction.status = 'finished';
        transaction.finishedAt = Date.now();
        this.updateTransactionDiagnostics(transaction);
        this.kkLastCompletedMediaTransaction = {
          ...transaction,
          resolveSettled: undefined,
          settled: undefined,
        };
        this.dataset.kkLastCompletedTransactionId = transaction.transactionId;
        this.dataset.kkLastCompletedTransactionGeneration = String(transaction.generation);
        this.restoreControlledSliderStyles(transaction);
        this.cancelSettleObserver();
        this.kkActiveMediaTransaction = null;
        this.updateNavigationDiagnostics(this.kkLastCompletedMediaTransaction);
        return true;
      }

      commitActiveMediaTransaction(transactionId) {
        const transaction = this.kkActiveMediaTransaction;
        if (
          !transaction ||
          transaction.transactionId !== transactionId ||
          transaction.generation !== this.kkGalleryGeneration
        ) {
          return;
        }
        transaction.status = 'committed';
        this.updateTransactionDiagnostics(transaction);
      }

      cancelSettleObserver() {
        if (this.kkSettleFrame) cancelAnimationFrame(this.kkSettleFrame);
        if (this.kkScrollFallbackTimer) clearTimeout(this.kkScrollFallbackTimer);
        this.kkSettleFrame = null;
        this.kkScrollFallbackTimer = null;
      }

      restoreControlledSliderStyles(transaction) {
        const slider = this.getMediaSlider();
        if (
          !slider ||
          !transaction ||
          transaction.generation !== this.kkGalleryGeneration
        ) {
          return;
        }
        if (transaction.previousScrollBehavior) {
          slider.style.setProperty('scroll-behavior', transaction.previousScrollBehavior);
        } else {
          slider.style.removeProperty('scroll-behavior');
        }
        if (transaction.previousScrollSnapType) {
          slider.style.setProperty('scroll-snap-type', transaction.previousScrollSnapType);
        } else {
          slider.style.removeProperty('scroll-snap-type');
        }
        this.restoreTargetDisplay(transaction);
      }

      revealTargetForPhysicalNavigation(transaction) {
        const target = this.findMedia(transaction?.expectedMediaId);
        if (!target || getComputedStyle(target).display !== 'none') return;
        const logicalMedia = this.findMedia(this.getLogicalActiveMediaId());
        if (logicalMedia && logicalMedia !== target) {
          transaction.concealedLogicalMedia = logicalMedia;
          transaction.previousLogicalDisplay = logicalMedia.style.getPropertyValue('display');
          logicalMedia.style.setProperty('display', 'none');
        }
        transaction.revealedTarget = target;
        transaction.previousTargetDisplay = target.style.getPropertyValue('display');
        target.style.setProperty('display', 'block');
      }

      restoreTargetDisplay(transaction) {
        const target = transaction?.revealedTarget;
        if (!target) return;
        if (transaction.previousTargetDisplay) {
          target.style.setProperty('display', transaction.previousTargetDisplay);
        } else {
          target.style.removeProperty('display');
        }
        const logicalMedia = transaction.concealedLogicalMedia;
        if (logicalMedia) {
          if (transaction.previousLogicalDisplay) {
            logicalMedia.style.setProperty('display', transaction.previousLogicalDisplay);
          } else {
            logicalMedia.style.removeProperty('display');
          }
        }
        transaction.revealedTarget = null;
        transaction.concealedLogicalMedia = null;
      }

      cancelActiveNavigation(status = 'superseded', details = {}) {
        const transaction = this.kkActiveMediaTransaction;
        if (!transaction) return false;

        this.cancelSettleObserver();
        transaction.status = status;
        this.updateTransactionDiagnostics(transaction);
        this.restoreControlledSliderStyles(transaction);
        const result = this.mediaActionResult('rejected-stale', transaction.expectedMediaId, transaction, {
          reason: status,
          newerUserTransaction: details.newerUserTransaction === true,
        });
        transaction.resolveSettled?.(result);
        if (
          this.kkActiveMediaTransaction?.transactionId === transaction.transactionId &&
          this.kkActiveMediaTransaction?.generation === transaction.generation
        ) {
          this.kkActiveMediaTransaction = null;
        }
        return true;
      }

      prepareControlledSlider(transaction) {
        const slider = this.getMediaSlider();
        if (!slider || !transaction) return false;

        transaction.previousScrollBehavior = slider.style.getPropertyValue('scroll-behavior');
        transaction.previousScrollSnapType = slider.style.getPropertyValue('scroll-snap-type');
        slider.style.setProperty('scroll-behavior', 'auto');
        slider.style.setProperty('scroll-snap-type', 'none');
        return true;
      }

      positionSliderForTransaction(transaction) {
        const slider = this.getMediaSlider();
        const target = this.findMedia(transaction?.expectedMediaId);
        if (
          !slider ||
          !target ||
          !transaction ||
          transaction.generation !== this.kkGalleryGeneration ||
          transaction.transactionId !== this.kkActiveMediaTransaction?.transactionId
        ) {
          return false;
        }

        const left = this.getTargetScrollLeft(target, slider);
        slider.style.setProperty('scroll-behavior', 'auto');
        slider.style.setProperty('scroll-snap-type', 'none');
        if (typeof slider.scrollTo === 'function') {
          slider.scrollTo({ left, behavior: 'auto' });
        }
        if (Math.abs(Number(slider.scrollLeft || 0) - left) > 0.5) {
          slider.scrollLeft = left;
        }
        this.updateNavigationDiagnostics(transaction);
        return true;
      }

      scheduleSettleCheck(transaction = this.kkActiveMediaTransaction) {
        if (
          !transaction ||
          transaction.transactionId !== this.kkActiveMediaTransaction?.transactionId ||
          transaction.generation !== this.kkGalleryGeneration ||
          this.kkSettleFrame
        ) {
          return;
        }
        this.kkSettleFrame = requestAnimationFrame((time) => {
          this.kkSettleFrame = null;
          this.checkNavigationSettle(transaction.transactionId, transaction.generation, time);
        });
      }

      checkNavigationSettle(transactionId, generation, time = performance.now()) {
        const transaction = this.kkActiveMediaTransaction;
        if (
          !transaction ||
          transaction.transactionId !== transactionId ||
          transaction.generation !== generation ||
          generation !== this.kkGalleryGeneration
        ) {
          return;
        }

        const slider = this.getMediaSlider();
        const target = this.findMedia(transaction.expectedMediaId);
        if (!slider || !target) {
          this.cancelActiveNavigation('missing-media');
          return;
        }

        this.dataset.kkSettleCheckCount = String(
          Number(this.dataset.kkSettleCheckCount || 0) + 1
        );
        const scrollLeft = Number(slider.scrollLeft || 0);
        const targetOffsetLeft = this.getTargetScrollLeft(target, slider);
        const physicalMediaId = this.getPhysicallyVisibleMediaId();
        const distance = Math.abs(scrollLeft - targetOffsetLeft);
        const scrollUnchanged =
          transaction.lastObservedScrollLeft != null &&
          Math.abs(scrollLeft - transaction.lastObservedScrollLeft) <= 0.5;
        const positionMatches =
          physicalMediaId === transaction.expectedMediaId && distance <= 1.5;

        this.updateNavigationDiagnostics(transaction);
        if (positionMatches) {
          if (!transaction.stableSince || !scrollUnchanged) {
            transaction.stableSince = time;
            transaction.stableObservations = 1;
          } else {
            transaction.stableObservations += 1;
          }
          transaction.lastObservedScrollLeft = scrollLeft;
          if (
            transaction.stableObservations >= 2 &&
            time - transaction.stableSince >= 96
          ) {
            this.completeNavigationTransaction(transaction);
            return;
          }
        } else {
          transaction.stableSince = null;
          transaction.stableObservations = 0;
          transaction.lastObservedScrollLeft = scrollLeft;
          this.positionSliderForTransaction(transaction);
        }
        this.scheduleSettleCheck(transaction);
      }

      completeNavigationTransaction(transaction) {
        const target = this.findMedia(transaction?.expectedMediaId);
        const slider = this.getMediaSlider();
        if (
          !target ||
          !slider ||
          transaction?.transactionId !== this.kkActiveMediaTransaction?.transactionId ||
          transaction?.generation !== this.kkGalleryGeneration
        ) {
          return false;
        }

        const physicalMediaId = this.getPhysicallyVisibleMediaId();
        const distance = Math.abs(
          Number(slider.scrollLeft || 0) - this.getTargetScrollLeft(target, slider)
        );
        if (physicalMediaId !== transaction.expectedMediaId || distance > 1.5) {
          this.positionSliderForTransaction(transaction);
          this.scheduleSettleCheck(transaction);
          return false;
        }

        const logicalBefore = this.getLogicalActiveMediaId();
        this.applyActiveMediaState(target);
        this.restoreTargetDisplay(transaction);
        this.preventStickyHeader();
        this.playActiveMedia(target);
        this.commitActiveMediaTransaction(transaction.transactionId);
        if (!transaction.notificationSent) {
          this.notifyActiveMediaChange(target.dataset.mediaId, this.contextFromTransaction(transaction));
        }
        const activeThumbnail = this.findThumbnail(target.dataset.mediaId);
        if (activeThumbnail) {
          this.announceLiveRegion(target, activeThumbnail.dataset.mediaPosition);
        }

        const result = this.mediaActionResult(
          logicalBefore === transaction.expectedMediaId ? 'already-active' : 'applied',
          transaction.expectedMediaId,
          transaction,
          {
            logicalMediaId: this.normalizeMediaId(target.dataset.mediaId),
            physicalMediaId,
            expectedMediaId: transaction.expectedMediaId,
          }
        );
        transaction.resolveSettled?.(result);
        this.finishActiveMediaTransaction(transaction.transactionId, transaction.generation);
        return true;
      }

      onViewerScroll() {
        const transaction = this.kkActiveMediaTransaction;
        if (transaction) {
          transaction.stableSince = null;
          transaction.stableObservations = 0;
          this.scheduleSettleCheck(transaction);
          if (this.kkScrollFallbackTimer) clearTimeout(this.kkScrollFallbackTimer);
          this.kkScrollFallbackTimer = setTimeout(() => {
            const current = this.kkActiveMediaTransaction;
            if (!current) return;
            this.positionSliderForTransaction(current);
            this.scheduleSettleCheck(current);
          }, 90);
          return;
        }
        if (this.kkUserGesture?.confirmed) this.scheduleUserScrollSettle();
        this.updateNavigationDiagnostics();
      }

      onViewerScrollEnd() {
        const transaction = this.kkActiveMediaTransaction;
        if (transaction) {
          transaction.scrollEndObserved = true;
          this.scheduleSettleCheck(transaction);
          return;
        }
        if (this.kkUserGesture?.confirmed) this.settleUserDrivenNavigation();
      }

      rejectStaleSlideChange(mediaId, transaction) {
        this.dataset.kkRejectedSlideMediaId = this.normalizeMediaId(mediaId);
        this.dataset.kkRejectedSlideExpectedMediaId = transaction?.expectedMediaId || '';
        this.dataset.kkRejectedSlideTransactionId = transaction?.transactionId || '';
        this.dataset.kkRejectedSlideGeneration = String(transaction?.generation || '');
        this.dataset.kkRejectedSlideSource = transaction?.source || '';
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        const mediaId = currentElement?.dataset.mediaId;
        if (!mediaId || !this.findMedia(mediaId)) return;
        const normalizedMediaId = this.normalizeMediaId(mediaId);
        const physicalMediaId = this.getPhysicallyVisibleMediaId();
        const transaction = this.kkActiveMediaTransaction;

        if (transaction && transaction.generation === this.kkGalleryGeneration) {
          if (
            normalizedMediaId !== transaction.expectedMediaId ||
            physicalMediaId !== transaction.expectedMediaId
          ) {
            this.rejectStaleSlideChange(normalizedMediaId, transaction);
            this.positionSliderForTransaction(transaction);
          }
          this.scheduleSettleCheck(transaction);
          return;
        }

        const gesture = this.kkUserGesture;
        if (gesture?.confirmed && physicalMediaId && physicalMediaId !== gesture.fromMediaId) {
          this.navigateToMedia(physicalMediaId, {
            origin: 'user',
            syncVariant: true,
            source: gesture.source || 'swipe',
            fromMediaId: gesture.fromMediaId,
            targetMediaId: physicalMediaId,
          });
          return;
        }

        const lastTransaction = this.kkLastCompletedMediaTransaction;
        if (
          lastTransaction?.expectedMediaId &&
          Date.now() - Number(lastTransaction.finishedAt || 0) < 2000 &&
          physicalMediaId !== lastTransaction.expectedMediaId
        ) {
          this.rejectStaleSlideChange(normalizedMediaId, lastTransaction);
          this.navigateToMedia(lastTransaction.expectedMediaId, {
            origin: 'internal',
            syncVariant: false,
            source: 'stale-slide-correction',
            fromMediaId: this.getLogicalActiveMediaId(),
            targetMediaId: lastTransaction.expectedMediaId,
          });
        }
      }

      applyActiveMediaState(activeMedia) {
        if (!activeMedia) return;
        this.getMediaItems().forEach((element) =>
          element.classList.toggle('is-active', element === activeMedia)
        );
        this.setActiveThumbnail(this.findThumbnail(activeMedia.dataset.mediaId));
      }

      notifyActiveMediaChange(mediaId, context = {}) {
        const normalizedMediaId = this.normalizeMediaId(mediaId);
        const normalizedContext = this.normalizeMediaContext(context);
        const announcementKey = [normalizedContext.transactionId, normalizedMediaId].join('|');
        this.kkMediaAnnouncements = this.kkMediaAnnouncements || new Set();
        if (!normalizedMediaId || this.kkMediaAnnouncements.has(announcementKey)) return false;

        this.kkMediaAnnouncements.add(announcementKey);
        if (this.kkMediaAnnouncements.size > 100) {
          this.kkMediaAnnouncements.delete(this.kkMediaAnnouncements.values().next().value);
        }
        this.kkLastAnnouncementKey = announcementKey;
        const transaction = this.kkActiveMediaTransaction;
        if (transaction?.transactionId === normalizedContext.transactionId) {
          transaction.notificationSent = true;
          transaction.status = 'committed';
          this.updateTransactionDiagnostics(transaction);
        }
        this.dataset.kkActiveMediaChangeCount = String(Number(this.dataset.kkActiveMediaChangeCount || 0) + 1);
        this.dataset.kkMediaNotifyCount = String(
          Number(this.dataset.kkMediaNotifyCount || 0) + 1
        );
        this.dispatchEvent(
          new CustomEvent('media-gallery:active-media-change', {
            bubbles: true,
            detail: {
              mediaId: normalizedMediaId,
              origin: normalizedContext.origin,
              syncVariant: normalizedContext.syncVariant,
              transactionId: normalizedContext.transactionId,
              generation: normalizedContext.generation,
              source: normalizedContext.source,
            },
          })
        );
        return true;
      }

      mediaActionResult(status, mediaId, context = {}, details = {}) {
        return {
          status,
          mediaId: this.normalizeMediaId(mediaId),
          transactionId: context.transactionId || null,
          requestToken: Number.isFinite(Number(context.requestToken))
            ? Number(context.requestToken)
            : null,
          variantId: context.variantId ? String(context.variantId) : null,
          ...details,
        };
      }

      rejectStaleVariantMediaContext(mediaId, context = {}) {
        if (context.origin !== 'variant' || !context.transactionId) return null;
        const transaction = this.kkActiveMediaTransaction;
        if (
          !transaction ||
          transaction.generation !== this.kkGalleryGeneration ||
          transaction.origin !== 'user' ||
          !['pending', 'committed'].includes(transaction.status) ||
          transaction.transactionId === context.transactionId
        ) {
          return null;
        }

        const requestToken = Number(context.requestToken);
        const requestTransaction = Number.isFinite(requestToken)
          ? this.kkVariantRequestTransactions?.get(requestToken)
          : null;
        const requestStartedBeforeUser =
          Boolean(requestTransaction) && requestTransaction.requestedAt < transaction.startedAt;
        const olderRequestToken =
          Number.isFinite(requestToken) &&
          Number.isFinite(this.kkLatestVariantRequestToken) &&
          requestToken < this.kkLatestVariantRequestToken;
        const olderGeneration =
          Number.isFinite(Number(context.generation)) &&
          Number(context.generation) < transaction.generation;
        if (
          context.latestApprovedVariant === true &&
          !requestStartedBeforeUser &&
          !olderRequestToken
        ) {
          return null;
        }
        if (!requestStartedBeforeUser && !olderRequestToken && !olderGeneration) return null;

        this.dataset.kkRejectedVariantMediaCount = String(
          Number(this.dataset.kkRejectedVariantMediaCount || 0) + 1
        );
        this.dataset.kkRejectedVariantTransactionId = context.transactionId;
        this.dataset.kkRejectedVariantMediaId = this.normalizeMediaId(mediaId);
        this.dataset.kkRejectedVariantRequestToken = Number.isFinite(requestToken)
          ? String(requestToken)
          : '';
        return this.mediaActionResult('rejected-stale', mediaId, context, {
          activeTransactionId: transaction.transactionId,
          activeGeneration: transaction.generation,
          newerUserTransaction: requestStartedBeforeUser,
        });
      }

      navigateToMedia(mediaId, context = {}) {
        const rejectedResult = this.rejectStaleVariantMediaContext(mediaId, context);
        if (rejectedResult) return rejectedResult;

        this.cancelActiveNavigation('superseded', {
          newerUserTransaction: context.origin === 'user',
        });
        const preserveOrder = this.kkPreserveMediaOrder;
        if (preserveOrder) this.restoreMediaOrder();
        let targetMedia = this.findMedia(mediaId);
        if (!targetMedia) return this.mediaActionResult('missing-media', mediaId, context);

        if (context.prepend === true && !preserveOrder) {
          targetMedia.parentElement.firstChild !== targetMedia &&
            targetMedia.parentElement.prepend(targetMedia);
          const activeThumbnail = this.findThumbnail(targetMedia.dataset.mediaId);
          if (this.elements.thumbnails) {
            activeThumbnail?.parentElement?.firstChild !== activeThumbnail &&
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          targetMedia = this.findMedia(mediaId);
        }

        const transactionContext = this.beginMediaTransaction({
          ...context,
          fromMediaId: context.fromMediaId || this.navigationAnchorMediaId(),
          targetMediaId: targetMedia.dataset.mediaId,
        });
        const transaction = this.kkActiveMediaTransaction;
        transaction.logicalMediaIdAtStart = this.getLogicalActiveMediaId();
        transaction.physicalMediaIdAtStart = this.getPhysicallyVisibleMediaId();
        if (!this.prepareControlledSlider(transaction)) {
          this.cancelActiveNavigation('missing-slider');
          return this.mediaActionResult('missing-media', mediaId, transactionContext);
        }
        this.positionSliderForTransaction(transaction);
        this.scheduleSettleCheck(transaction);
        return this.mediaActionResult('pending', targetMedia.dataset.mediaId, transactionContext, {
          settled: transaction.settled,
          generation: transaction.generation,
        });
      }

      setActiveMedia(mediaId, prepend, context = {}) {
        return this.navigateToMedia(mediaId, {
          ...context,
          prepend: prepend === true,
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        const slider = this.elements.thumbnails.slider;
        if (slider?.scrollTo) slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'auto' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
        };

        if (this.kkPreserveMediaOrder) {
          if (image.complete) {
            announce();
          } else {
            image.addEventListener('load', announce, { once: true });
          }
          return;
        }

        image.onload = announce;
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

(() => {
  const sourceScript = document.currentScript;
  const templateSuffix = document.body.dataset.kkTemplate;
  const premiumTemplates = [
    'bryllup-gjestebok',
    'dap-gjestebok',
    'konfirmasjon-gjestebok',
    'hyttebok-premium',
    'bobilbok-premium',
    'tilbehor-hytte-bobil-premium',
    'tilbehor-premium',
    'skilt-premium',
  ];
  const personalizationCounterTemplates = [
    'million',
    'navn-dato-konfirmasjon',
    'navn-dato-og-font-type',
    'navn-og-dato-font',
  ];
  if (!sourceScript?.src || window.__kkGalleryAddonsRequested === true) return;

  window.__kkGalleryAddonsRequested = true;
  const files = ['kk-font-picker.js'];
  if (premiumTemplates.includes(templateSuffix)) {
    files.unshift('kk-gallery-enhancements.js');
    files.push('kk-ui-fixes.js', 'kk-gallery-variant-sync.js');
    if (templateSuffix === 'konfirmasjon-gjestebok') files.splice(2, 0, 'kk-motif-selector.js');
  } else if (personalizationCounterTemplates.includes(templateSuffix)) {
    files.push('kk-ui-fixes.js');
  }
  const cacheVersion = '20260726gallerysettle1';

  const load = (index) => {
    if (index >= files.length) return;

    const script = document.createElement('script');
    const sourceUrl = new URL(files[index], sourceScript.src);
    sourceUrl.searchParams.set('v', cacheVersion);
    script.src = sourceUrl.href;
    script.async = false;
    script.onload = () => load(index + 1);
    script.onerror = () => load(index + 1);
    document.head.appendChild(script);
  };

  load(0);
})();
